import React from 'react';

import { useUser, DisplayUserName } from '@adsk/alloy-provider';
import { Text } from '@adsk/alloy-react-box';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { MainEntityDisplay } from '.';
import { IN_PROGRESS_STEP } from '../../consts';
import {
  useUsersData,
  useEntityData,
  useCompaniesData,
  useRolesData,
  useEntitiesData,
} from '../../hooks';
import { useTranslation } from '../../i18n';
import { SubmittalsItem, TaskStatus } from '../../types';
import { sortTasksByRequiredAndAlphabeticallyAllTypes } from '../../utils/steps';

export enum SIZES {
  SMALL = 'small',
  MEDIUM = 'medium',
}

interface MultipleAssigneeCellProps {
  userIds: string[];
  itemData?: SubmittalsItem;
  stacked?: boolean;
  size?: SIZES;
  isPendingActionFrom?: boolean;
}

const DEFAULT_PROPS = {
  userIds: [],
  stacked: false,
  size: SIZES.MEDIUM,
  isPendingActionFrom: false,
};

const MultipleAssigneeDisplay: React.FC<MultipleAssigneeCellProps> = ({
  userIds = DEFAULT_PROPS.userIds,
  itemData,
  stacked = DEFAULT_PROPS.stacked,
  size = DEFAULT_PROPS.size,
  isPendingActionFrom = DEFAULT_PROPS.isPendingActionFrom,
}) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const currentUserId = user?.autodeskId || '';
  const currentUser = useEntityData(currentUserId);
  const currentUserCompanyId = currentUser.company || '';
  const currentUserRoleIds = currentUser.roles || [];

  const isItemInReview = itemData && itemData?.stateId === 'rev';
  const ballInCourtCompanies =
    (itemData && itemData?.ballInCourtCompanies) || [];
  const ballInCourtUsers = (itemData && itemData?.ballInCourtUsers) || [];
  const ballInCourtRoles = (itemData && itemData?.ballInCourtRoles) || [];
  let sortedReviewerIds: string[] = [];

  const finalBallInCourtArray: string[] = [];

  if (isPendingActionFrom) {
    // TODO: userIds seems to be redundant and can be removed
    userIds = ballInCourtUsers;
  }

  const ballInCourtUsersData = useUsersData(userIds);
  const ballInCourtCompaniesData = useCompaniesData(ballInCourtCompanies);
  const ballInCourtRolesData = useRolesData(ballInCourtRoles);

  if (isItemInReview) {
    // to display reviewers of current step first
    const reviewSteps = itemData ? itemData.steps : [];
    if (reviewSteps) {
      const inProgressStep = reviewSteps.find((step) => {
        if (step.status === IN_PROGRESS_STEP) return step;
        return undefined;
      });
      if (inProgressStep) {
        const sortedTasksAtInProgressStep =
          sortTasksByRequiredAndAlphabeticallyAllTypes(
            inProgressStep,
            ballInCourtUsersData,
            ballInCourtCompaniesData,
            ballInCourtRolesData,
          );
        sortedTasksAtInProgressStep.forEach((task) => {
          if (!isPendingActionFrom || task.status === TaskStatus.IN_PROGRESS) {
            sortedReviewerIds = [...sortedReviewerIds, task.assignedTo];
          }
        });
      }
    }
  }

  // to bring logged in user to the first if ball in court user array
  const ballInCourtArray = isItemInReview
    ? sortedReviewerIds
    : [...userIds, ...ballInCourtCompanies, ...ballInCourtRoles];

  const userRoles = ballInCourtArray.filter((value) =>
    currentUserRoleIds.includes(value),
  );
  const currentUserRoleId = userRoles[0] || '';

  const indexOfCurrentUser = ballInCourtArray.indexOf(currentUserId);
  const indexOfCurrentUserRole = ballInCourtArray.indexOf(currentUserRoleId);
  const indexOfCurrentUserCompany =
    ballInCourtArray.indexOf(currentUserCompanyId);
  if (indexOfCurrentUser > 0) {
    finalBallInCourtArray.push(ballInCourtArray[indexOfCurrentUser]);
    const otherBallInCourtUsers = ballInCourtArray.filter(
      (userId) => userId !== currentUserId,
    );
    finalBallInCourtArray.push(...otherBallInCourtUsers);
  } else if (indexOfCurrentUserRole > 0) {
    finalBallInCourtArray.push(ballInCourtArray[indexOfCurrentUserRole]);
    const otherBallInCourtUsers = ballInCourtArray.filter(
      (userId) => userId !== currentUserRoleId,
    );
    finalBallInCourtArray.push(...otherBallInCourtUsers);
  } else if (indexOfCurrentUserCompany > 0) {
    finalBallInCourtArray.push(ballInCourtArray[indexOfCurrentUserCompany]);
    const otherBallInCourtUsers = ballInCourtArray.filter(
      (userId) => userId !== currentUserCompanyId,
    );
    finalBallInCourtArray.push(...otherBallInCourtUsers);
  } else {
    finalBallInCourtArray.push(...ballInCourtArray);
  }
  const mainEntityId = finalBallInCourtArray[0];
  const mainEntity = useEntityData(mainEntityId);
  const ballInCourtEntities = useEntitiesData(finalBallInCourtArray);
  const tooltipContent = (
    <>
      {ballInCourtEntities.map((entity) => (
        <div key={entity.id}>
          {ballInCourtUsers.includes(entity.id) ? (
            <DisplayUserName id={entity.id} />
          ) : (
            entity.name || <Text color="charcoal500">{t('FORMER_MEMBER')}</Text>
          )}
        </div>
      ))}
    </>
  );

  if (mainEntity.isLoading) {
    return <span>{t('LOADING')}</span>;
  }

  return (
    // to have tooltip and content even when the user name is not overflowing
    <Tooltip content={tooltipContent} trigger={Tooltip.TRIGGER_EVENTS.HOVER}>
      <OverflowTooltip autoTipOnEllipsis>
        <MainEntityDisplay
          mainEntityId={mainEntityId}
          isMainEntityUser={ballInCourtUsers.includes(mainEntityId)}
          currentUserId={currentUserId}
          currentUserCompanyId={currentUserCompanyId}
          currentUserRoleId={currentUserRoleId}
          totalEntities={finalBallInCourtArray}
          stacked={stacked}
          size={size}
        />
      </OverflowTooltip>
    </Tooltip>
  );
};

MultipleAssigneeDisplay.displayName = 'SubmittalsMultipleAssigneeDisplay';

export default MultipleAssigneeDisplay;
